import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { makeEnum, makeOptions,
  setupClass, lookupData, 
  interpolateInMap,
} from '../Base.js'
import { InputComponent } from '../Common/InputComponent.js'

import * as psy from './Psychrometrics.js'
import { CalcContext } from '../Common/CalcContext.js'

import { WallType } from './WallType.js'
import { Units } from '../Common/Units.js'

import { prettyJson, valOr,
  addElem, removeElem, elemIn,
  generateItemName, formatNum,
  sortItemsByName,
} from '../SharedUtils.js'

import {
  FieldType,
  Field,
  FieldGroup,
} from '../Common/Field.js'

import { gApp, DebugOn } from '../Globals.js'


let AdjacentTemperature = makeEnum({
  OutdoorTemp: 'Outdoor temperature',
  IndoorTemp: 'Indoor temperature',
  Other: 'Other',
})

class BufferSpaceWall {
  init() {
    // TODO - Clean up the UI here. Mixing Manual with wallType chooser.
    this.type = Field.makeTypeSelect('Type', gApp.proj().wallTypes, 'Manual', {
      metadata: {
        typeClass: WallType,
      },
      note: 'Select an existing wall type to inherit its R-value, or choose "Manual" to enter an R-value directly.',
    })
    this.rValue = new Field({
      name: 'R-value',
      type: FieldType.RValue,
      requiresInput: true,
      note: 'The R-value of this buffer-space wall. Editable when Type is Manual; otherwise calculated from the selected wall type.',
    })
    this.rValue.makeUpdater((field) => {
      if (this.type.value == 'Manual') {
        field.isOutput = false;
      } else {
        field.isOutput = true;

        let wallType = this.type.lookupValue();
        if (!wallType) {
          throw new Error(`Could not find wallType '${this.type.value}`);
        }
        field.setValueInUnits(wallType.getRValue(), Units.RValue);
      }
    })
    this.area = new Field({
      name: 'Area',
      type: FieldType.Area,
      requiresInput: true,
      note: 'The surface area of this buffer-space wall.',
    })
    this.adjacentTemp = new Field({
      name: 'Adjacent Temperature',
      type: FieldType.Select,
      choices: makeOptions(AdjacentTemperature),
      note: 'What the wall is adjacent to on its far side: outdoor air, indoor conditioned space, or a custom temperature.',
      notesData: {
        'AllOptions': makeOptions(AdjacentTemperature),
      },
    })
    this.summerAdjacentTemp = new Field({
      name: 'Summer adjacent temperature',
      type: FieldType.Temp,
      note: 'Custom summer temperature on the far side of this wall. Only relevant when Adjacent Temperature is "Other".',
    })
    this.winterAdjacentTemp = new Field({
      name: 'Summer adjacent temperature',
      type: FieldType.Temp,
      note: 'Custom winter temperature on the far side of this wall. Only relevant when Adjacent Temperature is "Other".',
    })

    this.serFields = [
      'type',
      'rValue',
      'area',
      'adjacentTemp',
      'summerAdjacentTemp',
      'winterAdjacentTemp',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Buffer Space Wall',
      path: 'buffer-space-wall',
    }
  }

  getArea() {
    return this.area.getValueInUnits(Units.ft2);
  }

  getRValue() {
    if (this.type.value == 'Manual') {
      return this.rValue.getValueInUnits(Units.RValue);
    } else {
      let wallType = this.type.lookupValue();
      return wallType.getRValue();
    }
  }

  getAdjTemp(isHeating, t_o, t_i) {
    if (this.adjacentTemp.value == AdjacentTemperature.OutdoorTemp) {
      return t_o;
    } else if (this.adjacentTemp.value == AdjacentTemperature.IndoorTemp) {
      return t_i;
    } else if (this.adjacentTemp.value == AdjacentTemperature.Other) {
      return isHeating ?
        this.winterAdjacentTemp.getValueInUnits(Units.F) :
        this.summerAdjacentTemp.getValueInUnits(Units.F);
    } else {
      throw new Error("Unexpected adjacentTemp: " + this.adjacentTemp.value);
    }
  }
}
setupClass(BufferSpaceWall)

class BufferSpaceBuilder {
  init() {
    this.walls = new ComponentArray(BufferSpaceWall);
    this.estimatedInfiltration = new Field({
      name: 'Estimated infiltration',
      type: FieldType.AirFlow,
      defaultValue: 0,
      requiresInput: false,
      note: 'Estimated air infiltration into the buffer space from outdoors. Increases the influence of outdoor temperature on the buffer-space temperature.',
    })

    this.outputSummerTemp = new Field({
      name: 'Buffer Space Summer Temperature',
      type: FieldType.Temp,
      isOutput: true,
      note: 'Calculated summer temperature of the buffer space, based on the wall layers and estimated infiltration.',
    })
    this.outputSummerTemp.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.requiresInput()) {
        field.waitingInputs = true;
        return;
      }
      let res = this.canCalcTemp();
      if (!res.result) {
        field.value = 0;
        field.setPriorityErrorMsg(res.reason);
        return;
      }
      let ctx = CalcContext.create();
      field.setValueInUnits(this._calcTempForUI(ctx, false), Units.F);
      field.clearPriorityErrorMsg();
      field.debugOutput = DebugOn() ? ctx.getLogStr() : null;
    })

    this.outputWinterTemp = new Field({
      name: 'Buffer Space Winter Temperature',
      type: FieldType.Temp,
      isOutput: true,
      note: 'Calculated winter temperature of the buffer space, based on the wall layers and estimated infiltration.',
    })
    this.outputWinterTemp.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.requiresInput()) {
        field.waitingInputs = true;
        return;
      }
      let res = this.canCalcTemp();
      if (!res.result) {
        field.value = 0;
        field.setPriorityErrorMsg(res.reason);
        return;
      }
      let ctx = CalcContext.create();
      field.setValueInUnits(this._calcTempForUI(ctx, true), Units.F);
      field.clearPriorityErrorMsg();
      field.debugOutput = DebugOn() ? ctx.getLogStr() : null;
    })

    // Add a default wall
    this.addWall();

    this.serFields = [
      ser.arrayField('walls', () => { return BufferSpaceWall.create(); }),
      'estimatedInfiltration',
    ];
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Buffer Space Builder',
      path: 'buffer-space-builder',
    }
  }

  addWall() {
    let wall = BufferSpaceWall.create();
    this.walls.push(wall);
    return wall;
  }

  removeWall(wall) {
    removeElem(this.walls, wall);
  }

  canCalcTemp() {
    let locationData = gApp.proj().getLocationData();
    if (!locationData.isLocationSet()) {
      return {result: false, reason: "Location is not set"};
    }
    if (this.walls.length == 0) {
      return {result: false, reason: "At least one wall must be added"};
    }
    for (const wall of this.walls) {
      if (wall.getArea() == 0 || wall.getRValue() == 0) {
        return {result: false, reason: "R-value and area must be set for all layers"};
      }
    }
    return {result: true, reason: null};
  }

  requiresInput() {
    for (const wall of this.walls) {
      if (wall.getArea() == 0 || wall.getRValue() == 0) {
        return true;
      }
    }
    return false;
  }

  _calcTempForUI(ctx, isHeating) {
    let locationData = gApp.proj().getLocationData();
    if (!locationData.isLocationSet()) {
      throw new Error("Location is not set");
    }
    let weatherData = gApp.proj().getLocationData().getOutputs();
    let sampleTemps = gApp.proj().getSampleTemps();
    ctx.elevation = weatherData.elevation;
    ctx.P_loc = psy.calcLocalPressure(ctx.elevation);
    ctx.C_s = 1.1 * ctx.P_loc / psy.P_std;
    if (isHeating) {
      ctx.t_i = sampleTemps.t_i_heating;
      ctx.t_o = sampleTemps.t_o_heating;
    } else {
      ctx.t_i = sampleTemps.t_i_cooling;
      ctx.t_o = sampleTemps.t_o_cooling;
    }
    return this.calcTemp(ctx, ctx.C_s, ctx.t_o, ctx.t_i, isHeating);
  }

  calcTemp(ctx, C_s, t_o, t_i, isHeating) {
    ctx.startSection();

    let wallData = [];
    for (let i = 0; i < this.walls.length; ++i) {
      let wall = this.walls[i];
      if (wall.getArea() == 0 || wall.getRValue() == 0) {
        throw new Error("Wall area and R-value must be set");
      }
      wallData.push({
        A: wall.getArea(),
        R: wall.getRValue(),
        U: 1.0 / wall.getRValue(),
        t: wall.getAdjTemp(isHeating, t_o, t_i),
      });
    }
    ctx.wallData = wallData

    ctx.X_1 = ctx.evalSum('A*U*t', wallData, 'X_1')
    ctx.X_2 = ctx.evalSum('A*U', wallData, 'X_2');

    ctx.Q = this.estimatedInfiltration.value;
    //console.log(`C_s=${ctx.C_s}, Q=${ctx.Q}, X_2=${ctx.X_2}`);
    ctx.t_b = ctx.eval('(C_s*Q*t_o + X_1) / (C_s*Q + X_2)',
      {C_s, t_o}, 't_b');
    let res = ctx.t_b;
    ctx.endSection();
    return res;
  }
}
setupClass(BufferSpaceBuilder)

export let BufferSpaceEntryMethod = makeEnum({
  Manual: 'Manual',
  BuildBufferSpace: 'Build buffer space',
})

export class BufferSpaceType extends InputComponent {
  init(name, makeId) {
    this.id = makeId ? gApp.proj().makeId('BufferSpaceType') : 0;

    this.name = Field.makeName('Name', name)

    this.entryMethod = new Field({
      name: 'Entry method',
      type: FieldType.Select,
      choices: makeOptions(BufferSpaceEntryMethod),
      bold: true,
      note: 'Whether to enter buffer-space temperatures directly, or to compute them by building up the buffer-space walls.',
    })

    this.manualGroup = new FieldGroup([
      new Field({
        key: 'summerTemperature',
        name: 'Buffer Space Summer Temperature',
        type: FieldType.Temp,
        requiresInput: true,
        note: 'The summer temperature inside the buffer space. Only relevant when entry method is Manual.',
      }),
      new Field({
        key: 'winterTemperature',
        name: 'Buffer Space Winter Temperature',
        type: FieldType.Temp,
        requiresInput: true,
        note: 'The winter temperature inside the buffer space. Only relevant when entry method is Manual.',
      })
    ])
    this.manualGroup.setVisibility(() => {
      return this.entryMethod.value == BufferSpaceEntryMethod.Manual;
    })

    this.builder = BufferSpaceBuilder.create();
    this.updater.setEnabledWhen('builder', this.builder, () => {
      return this.entryMethod.value == BufferSpaceEntryMethod.BuildBufferSpace;
    });

    this.serFields = [
      'id',
      'name',
      'entryMethod',
      'manualGroup',
      'builder',
    ];
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Buffer Space',
      uniquePath: true,
      path: () => {
        return `bufferspaces/${this.id}`;
      },
    }
  }

  static getTypeMetadata() {
    return {
      name: 'Buffer Space',
      createType: BufferSpaceType.createType,
      openEditor: BufferSpaceType.openEditor,
      customEditor: true,
    }
  }

  static createType() {
    let type = BufferSpaceType.create(generateItemName('BufferSpace', gApp.proj().bufferSpaceTypes), true)
    gApp.proj().bufferSpaceTypes.push(type);
    sortItemsByName(gApp.proj().bufferSpaceTypes);
    return type
  }

  static openEditor(type) {
    let pathName = gApp.proj().isCommercial() ? 'building-bufferspaces' : 'bufferspaces';
    gApp.proj().editorState.selectedBufferSpace = type;
    gApp.router.push({name: pathName});
  }

  getInputPage() {
    return {
      label: `${this.name.value}`,
      // TODO
      path: `bufferspaces/${this.id}`,
      typeName: 'Buffer Space',
      typeObject: this,
    };
  }

  calcTemps(ctx, C_s, t_o, t_i, isHeating) {
    if (this.entryMethod.value == BufferSpaceEntryMethod.Manual) {
      return isHeating ? this.manualGroup.getField('winterTemperature').value :
        this.manualGroup.getField('summerTemperature').value;
    } else {
      return this.builder.calcTemp(ctx, C_s, t_o, t_i, isHeating);
    }
  }

  _getSummaryData() {
    if (this.entryMethod.value == BufferSpaceEntryMethod.Manual) {
      return {
        summerTemp: this.manualGroup.getField('summerTemperature').getDisplayValueStr(),
        winterTemp: this.manualGroup.getField('winterTemperature').getDisplayValueStr(),
      }
    } else {
      return {
        summerTemp: this.builder.outputSummerTemp.getDisplayValueStr(),
        winterTemp: this.builder.outputWinterTemp.getDisplayValueStr(),
      }
    }
  }

  static getTableInfo() {
    return {
      typeName: 'Buffer Space',
      pagePath: 'bufferspaces',
      newItemName: 'BufferSpace-$CTR',
      allowDuplicate: true,
      columns: {
        'name': {
          label: 'Name',
        },
        'winterTemp': {
          label: 'Winter Temperature',
        },
        'summerTemp': {
          label: 'Summer Temperature',
        },
      }
    }
  }

  getTableData() {
    let summaryData = this._getSummaryData();
    return {
      name: this.name.value,
      winterTemp: summaryData.winterTemp,
      summerTemp: summaryData.summerTemp,
    }
  }
}
setupClass(BufferSpaceType)
